// Owen AI - Your AI-Powered Mortgage Advisor

export const OWEN_CHARACTER = {
  name: 'Owen',
  title: 'AI-Powered Mortgage Advisor',
  avatar: '/assets/owen-avatar.png',
  personality: {
    tone: 'friendly, professional, and knowledgeable',
    style: 'conversational yet trustworthy',
    approach: 'empowering and educational',
  },
};

// Owen's contextual messages for each step
export const OWEN_MESSAGES = {
  welcome: {
    greeting: "Hi! I'm Owen, your AI-powered mortgage advisor. I'm here to guide you through every step of your home financing journey.",
    intro: "Unlike traditional lenders who bombard you with hundreds of questions, I'll keep this simple and focused. Let's start with just a few key questions to understand your situation.",
    timeEstimate: "This will take about 5-10 minutes, and you can save and return anytime.",
  },

  quickStart: {
    intro: "Let's get to know your financial situation. I'll only ask what's necessary to find your perfect loan match.",
    encouragement: "You're doing great! Each answer helps me narrow down the best options for you.",
    saveReminder: "Remember, you can save your progress at any time and come back when it's convenient.",
  },

  analyzing: {
    processing: "Give me just a moment while I analyze thousands of loan products to find your perfect match...",
    comparing: "I'm comparing conventional, FHA, VA, USDA, jumbo, and specialty programs...",
    calculating: "Analyzing interest rates, down payment requirements, PMI costs, and long-term financial impact...",
    complete: "Analysis complete! I've found the best options for your unique situation.",
  },

  recommendation: {
    intro: "Based on my analysis of your financial profile, property goals, and current market conditions, here are my recommendations.",
    explanation: "Let me explain why each of these products fits your situation:",
    confidence: "I've analyzed thousands of successful loan files to make these recommendations with confidence.",
  },

  application: {
    intro: "Now that you've selected a loan product, I'll only ask questions that matter for your specific loan type.",
    efficiency: "Unlike traditional lenders who ask hundreds of irrelevant questions, we'll focus on what's actually needed.",
    progress: "You're making excellent progress. Let's keep going!",
  },

  documents: {
    intro: "You have two paths forward, both designed to give you maximum flexibility:",
    freeOption: "Get a personalized checklist (completely free) and gather documents at your own pace.",
    paidOption: "Upload documents now for early verification (small fee, fully refunded when you select a lender).",
    benefit: "Early verification catches issues before they become problems and speeds up your closing.",
  },

  lenderNetwork: {
    intro: "Here's where things get exciting. Your profile will be submitted anonymously to our network of vetted lenders.",
    privacy: "Your personal information stays private until YOU choose to reveal it.",
    research: "I've done extensive research on each lender - customer reviews, closing rates, BBB ratings, and online reputation.",
    power: "Instead of begging for approval, lenders will compete for your business.",
  },

  underwriting: {
    intro: "This is where LoanKnow truly revolutionizes the mortgage process.",
    choice: "You can choose me for AI underwriting, or select a certified human underwriter for maximum assurance.",
    aiAdvantage: "I can analyze your file in minutes using advanced AI trained on millions of successful loans.",
    humanAdvantage: "Human underwriters bring years of experience and are perfect for complex situations.",
    benefit: "Either way, you'll receive a Readiness Score that puts you in the strongest negotiating position.",
  },

  readinessScore: {
    intro: "Your Readiness Score is a comprehensive analysis of your loan's strength.",
    components: "It includes potential issues flagged early, recommendations for improvement, and your competitive advantage.",
    impact: "This score transforms you from a hopeful applicant into a pre-vetted, low-risk borrower.",
  },

  submission: {
    intro: "Armed with your fully underwritten file and Readiness Score, you're now in an unprecedented position of strength.",
    advantage: "Lenders see you as low-risk and high-value. The power dynamic has completely flipped in your favor.",
    refund: "All fees you've paid to LoanKnow will be fully refunded by participating lenders.",
  },

  closing: {
    intro: "You're in the home stretch! Review competitive offers and select your preferred lender.",
    speed: "Because you're already underwritten, there's no waiting for approval. You'll close in 7-10 days instead of 30-45.",
    confidence: "Lock your rate when you're ready and watch everything move at incredible speed.",
  },
};

// Owen's insights for different scenarios
export const OWEN_INSIGHTS = {
  creditScore: {
    excellent: "Your excellent credit score opens doors to the best rates and terms available. You're in a strong position!",
    good: "Your good credit score qualifies you for competitive rates across multiple loan programs.",
    fair: "Your credit score qualifies you for several programs, including FHA which is designed for borrowers in your range.",
    building: "I see opportunities here. FHA and some specialty programs are designed specifically for credit profiles like yours.",
  },

  downPayment: {
    high: "Your substantial down payment eliminates PMI and gives you access to the lowest rates. Excellent planning!",
    standard: "Your down payment is right in the sweet spot for conventional financing.",
    low: "No problem! Several programs like FHA and VA are designed for lower down payments.",
    zero: "Great news - VA and USDA loans offer 0% down for eligible borrowers.",
  },

  employment: {
    w2: "W-2 employment makes income verification straightforward. We'll need your recent pay stubs and W-2s.",
    selfEmployed: "Self-employed? I have options that work with bank statements instead of tax returns.",
    retired: "Retirement income is perfectly acceptable. We'll document your pension, Social Security, and investment income.",
  },

  propertyType: {
    investment: "Investment property? I'll focus on programs that consider rental income and cash flow.",
    primary: "Primary residence gets you the best rates and most favorable terms.",
    vacation: "Second homes have slightly different requirements, but I've got you covered.",
  },
};

// Owen's tips throughout the process
export const OWEN_TIPS = [
  "💡 Tip: Higher credit scores can save you thousands in interest over the life of your loan.",
  "💡 Tip: 20% down payment eliminates PMI on conventional loans, reducing your monthly payment.",
  "💡 Tip: Getting pre-underwritten can help you win bidding wars in competitive markets.",
  "💡 Tip: Shorter rate locks often mean better rates. Our fast process takes advantage of this.",
  "💡 Tip: All fees paid to LoanKnow are refunded by lenders - they're happy to pay for quality files.",
  "💡 Tip: Closing in 7-10 days vs 30-45 days can save you money on rate locks and rental overlap.",
  "💡 Tip: Your Readiness Score gives you negotiating power that traditional applicants don't have.",
];

// Owen's explanations for why certain questions are asked
export const OWEN_EXPLANATIONS = {
  creditScore: "Your credit score is the single biggest factor in determining your interest rate. It tells lenders how reliably you've managed credit in the past.",
  income: "Income verification ensures you can comfortably afford your monthly payments. Lenders typically want your total debt payments below 43-50% of gross income.",
  employment: "Employment history shows income stability. Most programs want to see 2 years of consistent employment or self-employment.",
  downPayment: "Down payment affects your loan-to-value ratio (LTV), which impacts your rate and whether you'll need mortgage insurance.",
  propertyValue: "Property value determines your loan amount and LTV ratio. It also tells me if you'll need a jumbo loan for amounts over $766,550.",
  propertyType: "Different property types have different lending requirements. Single-family homes typically get the best terms.",
  propertyUse: "Primary residences get the best rates. Investment properties require larger down payments but can use rental income for qualification.",
  debts: "Monthly debt obligations affect your debt-to-income ratio. Lower DTI means you can afford a larger mortgage payment.",
  assets: "Assets show you have reserves for down payment, closing costs, and emergencies. Lenders want to see 2-6 months of reserves.",
};

// Owen's congratulations and encouragement
export const OWEN_ENCOURAGEMENT = {
  questionComplete: "Perfect! That helps me narrow down your options.",
  sectionComplete: "Great work! You've completed this section.",
  halfwayPoint: "You're halfway there! Keep going.",
  almostDone: "Almost done! Just a few more questions.",
  applicationComplete: "Excellent! Your application is complete.",
  documentsUploaded: "Documents received! I'll verify these right away.",
  underwritingComplete: "Underwriting complete! Your Readiness Score is ready.",
  lenderSelected: "Great choice! Let's move forward with this lender.",
};

// Generate contextual Owen message based on step and data
export function getOwenMessage(step, context = {}) {
  const messages = OWEN_MESSAGES[step];
  if (!messages) return null;

  // Add personalization based on context
  if (context.userName) {
    return {
      ...messages,
      greeting: `Hi ${context.userName}! ${messages.greeting}`,
    };
  }

  return messages;
}

// Get Owen's insight based on user data
export function getOwenInsight(category, value) {
  const insights = OWEN_INSIGHTS[category];
  if (!insights) return null;

  return insights[value] || null;
}

// Get random Owen tip
export function getRandomOwenTip() {
  return OWEN_TIPS[Math.floor(Math.random() * OWEN_TIPS.length)];
}

// Get Owen's explanation for a specific field
export function getOwenExplanation(fieldId) {
  return OWEN_EXPLANATIONS[fieldId] || null;
}
